import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, FileDown, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { FeeCardPreview } from "@/pages/FeesCard";

// Reusable "Fee Card" button + popup. Drop <FeeCardButton studentId={id} /> anywhere.
export function FeeCardButton({ studentId, label = "Fee Card", variant = "outline", size = "sm", className = "", testId = "fee-card-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} size={size} className={`gap-1.5 ${className}`} onClick={() => setOpen(true)} data-testid={testId}>
        <CreditCard size={14} />{label}
      </Button>
      {open && <FeeCardDialog studentId={studentId} onClose={() => setOpen(false)} />}
    </>
  );
}

function FeeCardDialog({ studentId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [bundle, setBundle] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tpls, st, hd, fq, pv, pay] = await Promise.all([
          api.get("/fee-card-templates"),
          api.get("/settings"),
          api.get("/fee-heads"),
          api.get("/fee-head-frequencies"),
          api.get(`/fees/preview/${studentId}`),
          api.get("/payments", { params: { student_id: studentId } }),
        ]);
        if (!active) return;
        const templates = tpls.data || [];
        const tpl =
          templates.find((t) => t.style === "grid" && t.is_default) ||
          templates.find((t) => t.style === "grid") ||
          templates.find((t) => t.is_default) ||
          templates[0];
        setBundle({
          tpl,
          settings: st.data || {},
          heads: hd.data || [],
          headFreqs: fq.data || {},
          data: { student: pv.data.student, fees: pv.data.fees, history: pay.data },
        });
      } catch (e) {
        toast.error("Failed to load fee card");
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const printCard = () => {
    const node = cardRef.current;
    if (!node) return;
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) { toast.error("Please allow pop-ups to print"); return; }
    w.document.write(
      `<html><head><title>Fee Card</title><style>@page{size:A4 landscape;margin:8mm;}body{margin:0;padding:0;font-family:system-ui,Arial,sans-serif;}</style></head><body>${node.innerHTML}</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const savePdf = async () => {
    const node = cardRef.current;
    if (!node) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pw / canvas.width, ph / canvas.height);
      const w2 = canvas.width * ratio;
      const h2 = canvas.height * ratio;
      pdf.addImage(img, "PNG", (pw - w2) / 2, 16, w2, h2);
      const name = (bundle?.data?.student?.name || "student").replace(/\s+/g, "_");
      pdf.save(`FeeCard_${name}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-auto" data-testid="student-fee-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard size={18} className="text-indigo-600" />
            Monthly Fee Card{bundle?.data?.student ? ` · ${bundle.data.student.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        {loading || !bundle ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : (
          <>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={printCard} data-testid="fee-card-dialog-print">
                <Printer size={15} className="mr-1.5" />Print
              </Button>
              <Button size="sm" onClick={savePdf} disabled={pdfBusy} data-testid="fee-card-dialog-pdf">
                {pdfBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileDown size={15} className="mr-1.5" />}Save as PDF
              </Button>
            </div>
            <div className="overflow-auto rounded-lg bg-slate-50 p-3">
              <div ref={cardRef}>
                <FeeCardPreview tpl={bundle.tpl} data={bundle.data} settings={bundle.settings} heads={bundle.heads} headFreqs={bundle.headFreqs} />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
